/**
 * Confirm the opening cost of the selected unconfirmed layers (batch 8 T11, #3032).
 *
 * The user decides every cost. Nothing is pre-filled: each layer needs its own typed cost (or a hint the
 * user deliberately adopts, which only fills the input and the basis and still has to be confirmed), a
 * basis, and a note where one is required (a zero cost, basis "other"). Layers whose units were already
 * consumed show how many, and one acknowledgement says the user understands their cost changes from
 * unknown to known. The batch is all or nothing: the primary button states the layer count and the total
 * value it will record. The request the dialog builds is handed to the owner of the confirmation flow,
 * which keeps its key and payload across an uncertain result (`useConfirmFlow`).
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { describeApiError } from '@/lib/describeApiError';
import {
  COST_CONFIRMATION_BASES,
  type ConfirmItem,
  type ConfirmRequest,
  type CostConfirmationBasis,
  type OpeningLayer,
} from '@/hooks/useInventoryCosting';
import {
  euroInputFromCents4,
  formatEuroFromCents,
  formatEuroFromCents4,
  parseCents4Text,
  parseEuroInput,
  roundCents4ToCents,
  valueCents4,
  type EuroParseFailure,
} from './costMoney';
import { hintCents4, type HintSource } from './hints';
import type { ConfirmFlow } from './useConfirmFlow';

const BASIS_OF_HINT: Record<HintSource, CostConfirmationBasis> = {
  last_receipt: 'last_receipt_selected',
  catalog_cost: 'catalog_cost_selected',
  supplier_price: 'supplier_price_selected',
};

const NOTE_MAX = 1000;
const EVIDENCE_MAX = 200;

interface RowDraft {
  cost: string;
  basis: CostConfirmationBasis | '';
  note: string;
  evidenceRef: string;
}

const emptyDraft = (): RowDraft => ({ cost: '', basis: '', note: '', evidenceRef: '' });

interface RowCheck {
  readonly cents4: bigint | null;
  readonly text: string | null;
  readonly costError: EuroParseFailure | null;
  readonly isZero: boolean;
  readonly basisMissing: boolean;
  readonly noteRequired: boolean;
  readonly noteMissing: boolean;
  readonly tooLong: boolean;
  readonly valid: boolean;
}

function checkRow(draft: RowDraft): RowCheck {
  const parsed = parseEuroInput(draft.cost);
  const cents4 = parsed.ok ? parsed.cents4 : null;
  const isZero = cents4 === 0n;
  const note = draft.note.trim();
  const noteRequired = isZero || draft.basis === 'other';
  const noteMissing = noteRequired && note === '';
  const tooLong = draft.note.length > NOTE_MAX || draft.evidenceRef.length > EVIDENCE_MAX;
  const basisMissing = draft.basis === '';
  return {
    cents4,
    text: parsed.ok ? parsed.text : null,
    costError: parsed.ok ? null : parsed.reason,
    isZero,
    basisMissing,
    noteRequired,
    noteMissing,
    tooLong,
    valid: parsed.ok && !basisMissing && !noteMissing && !tooLong,
  };
}

export interface OpeningCostConfirmDialogProps {
  readonly open: boolean;
  /** The selected layers, as last read. Refreshing them (new consumed quantities) is the owner's job. */
  readonly rows: readonly OpeningLayer[];
  readonly flow: ConfirmFlow;
  readonly onClose: () => void;
  readonly onSubmit: (request: ConfirmRequest) => void;
  readonly onRemoveRow: (layerId: string) => void;
}

export function OpeningCostConfirmDialog({ open, rows, flow, onClose, onSubmit, onRemoveRow }: OpeningCostConfirmDialogProps) {
  const { t, i18n } = useTranslation('inventoryCosting');
  const { t: tc } = useTranslation('common');
  const language = i18n.language;

  const [drafts, setDrafts] = useState<Record<string, RowDraft>>({});
  const [adopted, setAdopted] = useState<Record<string, HintSource | undefined>>({});
  const [acknowledged, setAcknowledged] = useState(false);

  // A fresh dialog starts empty: no cost, no basis, no acknowledgement.
  useEffect(() => {
    if (!open) {
      setDrafts({});
      setAdopted({});
      setAcknowledged(false);
    }
  }, [open]);

  const consumedSignature = rows.map((row) => `${row.layerId}:${String(row.quantityConsumed)}`).join('|');
  // The acknowledgement covers the consumption the user saw: any change of it asks again.
  useEffect(() => {
    setAcknowledged(false);
  }, [consumedSignature]);

  const draftOf = (id: string): RowDraft => drafts[id] ?? emptyDraft();
  const patch = (id: string, change: Partial<RowDraft>) => setDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] ?? emptyDraft()), ...change } }));

  const checks = useMemo(() => rows.map((row) => ({ row, check: checkRow(drafts[row.layerId] ?? emptyDraft()) })), [rows, drafts]);
  const allValid = checks.length > 0 && checks.every(({ check }) => check.valid);
  const consumedRows = rows.filter((row) => row.quantityConsumed > 0);
  const consumedTotal = consumedRows.reduce((sum, row) => sum + row.quantityConsumed, 0);
  const ackNeeded = consumedRows.length > 0;
  const remainingTotal = rows.reduce((sum, row) => sum + row.quantityRemaining, 0);

  const exactTotal = checks.reduce((sum, { row, check }) => (check.cents4 === null ? sum : sum + valueCents4(check.cents4, row.quantityRemaining)), 0n);
  const typedCount = checks.filter(({ check }) => check.cents4 !== null).length;
  const roundedTotal = roundCents4ToCents(exactTotal);

  const locked = flow.pending !== null; // a sent request exists: the form is read only
  const sending = flow.phase === 'sending';
  const canSubmit = allValid && (!ackNeeded || acknowledged) && !locked && !sending;

  const adopt = (row: OpeningLayer, source: HintSource) => {
    const cents4 = hintCents4(row, source);
    if (cents4 === null) return;
    patch(row.layerId, { cost: euroInputFromCents4(cents4), basis: BASIS_OF_HINT[source] });
    setAdopted((prev) => ({ ...prev, [row.layerId]: source }));
  };
  const adoptForAll = (source: HintSource) => {
    for (const row of rows) adopt(row, source);
  };
  const editCost = (row: OpeningLayer, value: string) => {
    const was = adopted[row.layerId];
    const draft = draftOf(row.layerId);
    // Typing over an adopted hint ends the claim that the cost came from it: the basis must be chosen again.
    if (was && draft.basis === BASIS_OF_HINT[was]) {
      patch(row.layerId, { cost: value, basis: '' });
      setAdopted((prev) => ({ ...prev, [row.layerId]: undefined }));
    } else {
      patch(row.layerId, { cost: value });
    }
  };

  const submit = () => {
    if (!canSubmit) return;
    const items: ConfirmItem[] = checks.map(({ row, check }) => {
      const draft = draftOf(row.layerId);
      const note = draft.note.trim();
      const evidence = draft.evidenceRef.trim();
      return {
        layerId: row.layerId,
        unitCostCents: check.text ?? '',
        basis: draft.basis as CostConfirmationBasis,
        ...(note !== '' ? { note } : {}),
        ...(evidence !== '' ? { evidenceRef: evidence } : {}),
        expectedConsumedQuantity: row.quantityConsumed,
      };
    });
    onSubmit({ items, acknowledgeConsumedQuantity: ackNeeded && acknowledged });
  };

  const refusalText = flow.refusal
    ? describeApiError(flow.refusal, tc, { codePrefix: 'inventoryCosting:errors', fallback: t('errors.generic') })
    : null;
  const costErrorText = (reason: EuroParseFailure | null): string | undefined => {
    if (reason === null) return undefined;
    return t(`confirm.costError.${reason}`);
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={sending ? undefined : onClose}
      maxWidth="md"
      fullWidth
      mobileTitle={t('confirm.title', { count: rows.length })}
      aria-labelledby="opening-cost-confirm-title"
    >
      <DialogTitle id="opening-cost-confirm-title">{t('confirm.title', { count: rows.length })}</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('confirm.intro')}
        </Alert>

        {flow.pending && flow.phase !== 'sending' && (
          <Alert severity="warning" sx={{ mb: 2 }} role="alert">
            <Typography variant="subtitle2">{t('pending.title')}</Typography>
            <Typography variant="body2">{flow.restored ? t('pending.restored') : t('pending.unknown')}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t('pending.sameRequest', { count: flow.pending.request.items.length })}
            </Typography>
          </Alert>
        )}
        {flow.persistence === 'unavailable' && flow.pending && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('pending.notStored')}
          </Alert>
        )}
        {refusalText && (
          <Alert severity="error" sx={{ mb: 2 }} role="alert" onClose={flow.dismissRefusal}>
            {refusalText}
          </Alert>
        )}

        {rows.length > 1 && rows.some((row) => row.hints) && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('confirm.adoptAllTitle', { count: rows.length })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('confirm.adoptAllHint')}
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              {(['last_receipt', 'catalog_cost', 'supplier_price'] as const).map((source) => {
                const available = rows.filter((row) => hintCents4(row, source) !== null).length;
                return (
                  <Button key={source} size="small" variant="outlined" disabled={locked || sending || available === 0} onClick={() => adoptForAll(source)}>
                    {t(`confirm.adoptAll.${source}`, { count: available })}
                  </Button>
                );
              })}
            </Stack>
          </Paper>
        )}

        <Stack spacing={2}>
          {checks.map(({ row, check }) => {
            const draft = draftOf(row.layerId);
            const costTouched = draft.cost !== '';
            return (
              <Paper key={row.layerId} variant="outlined" sx={{ p: 2 }} aria-label={`${row.sku} ${row.name}`}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2">
                      {row.sku} · {row.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('confirm.rowFacts', {
                        warehouse: row.warehouseName,
                        remaining: row.quantityRemaining,
                        warehouses: row.lineage.warehouseCount,
                      })}
                    </Typography>
                  </Box>
                  <Button size="small" disabled={locked || sending} onClick={() => onRemoveRow(row.layerId)}>
                    {t('confirm.removeRow')}
                  </Button>
                </Box>

                {row.quantityConsumed > 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {t('confirm.consumedRow', { consumed: row.quantityConsumed })}
                  </Alert>
                )}
                {row.exhausted && <Chip size="small" color="default" label={t('confirm.exhausted')} sx={{ mb: 1 }} />}

                {row.hints && (
                  <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 1.5 }} aria-label={t('confirm.hintsLabel')}>
                    <HintChip
                      source="last_receipt"
                      label={
                        row.hints.lastReceipt
                          ? t('hints.lastReceipt', {
                              value: formatEuroFromCents4(parseCents4Text(row.hints.lastReceipt.unitCostCents4) ?? 0n, language),
                              date: new Date(row.hints.lastReceipt.receivedAt).toLocaleDateString(language),
                            })
                          : t('hints.lastReceiptNone')
                      }
                      enabled={hintCents4(row, 'last_receipt') !== null && !locked && !sending}
                      useLabel={t('confirm.useHint')}
                      onUse={() => adopt(row, 'last_receipt')}
                    />
                    <HintChip
                      source="catalog_cost"
                      label={
                        row.hints.catalogCost.status === 'recorded' && row.hints.catalogCost.unitCostCents !== null
                          ? t('hints.catalogCost', {
                              value: formatEuroFromCents(row.hints.catalogCost.unitCostCents, language),
                              date: row.hints.catalogCost.recordedAt
                                ? new Date(row.hints.catalogCost.recordedAt).toLocaleDateString(language)
                                : t('hints.noDate'),
                            })
                          : t('hints.catalogCostUnknown')
                      }
                      enabled={hintCents4(row, 'catalog_cost') !== null && !locked && !sending}
                      useLabel={t('confirm.useHint')}
                      onUse={() => adopt(row, 'catalog_cost')}
                    />
                    <HintChip
                      source="supplier_price"
                      label={
                        row.hints.preferredSupplier
                          ? t('hints.supplierPrice', {
                              supplier: row.hints.preferredSupplier.supplierName,
                              value: formatEuroFromCents(row.hints.preferredSupplier.unitCostCents, language),
                            })
                          : t('hints.supplierPriceNone')
                      }
                      enabled={hintCents4(row, 'supplier_price') !== null && !locked && !sending}
                      useLabel={t('confirm.useHint')}
                      onUse={() => adopt(row, 'supplier_price')}
                    />
                  </Stack>
                )}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    label={t('confirm.costLabel')}
                    value={draft.cost}
                    onChange={(event) => editCost(row, event.target.value)}
                    disabled={locked || sending}
                    required
                    error={costTouched && check.costError !== null}
                    helperText={costTouched ? costErrorText(check.costError) ?? t('confirm.costHint') : t('confirm.costHint')}
                    slotProps={{
                      htmlInput: { inputMode: 'decimal', autoComplete: 'off', 'aria-label': `${t('confirm.costLabel')} ${row.sku}` },
                      input: { endAdornment: <Typography color="text.secondary">€</Typography> },
                    }}
                  />
                  <TextField
                    select
                    label={t('confirm.basisLabel')}
                    value={draft.basis}
                    onChange={(event) => patch(row.layerId, { basis: event.target.value as CostConfirmationBasis })}
                    disabled={locked || sending}
                    required
                    helperText={t('confirm.basisHint')}
                    slotProps={{ htmlInput: { 'aria-label': `${t('confirm.basisLabel')} ${row.sku}` } }}
                  >
                    {COST_CONFIRMATION_BASES.map((basis) => (
                      <MenuItem key={basis} value={basis}>
                        {t(`basis.${basis}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label={t('confirm.noteLabel')}
                    value={draft.note}
                    onChange={(event) => patch(row.layerId, { note: event.target.value })}
                    disabled={locked || sending}
                    required={check.noteRequired}
                    error={check.noteMissing || draft.note.length > NOTE_MAX}
                    multiline
                    minRows={1}
                    helperText={check.noteMissing ? t('confirm.noteRequired') : t('confirm.noteHint')}
                    slotProps={{ htmlInput: { maxLength: NOTE_MAX + 1, 'aria-label': `${t('confirm.noteLabel')} ${row.sku}` } }}
                  />
                  <TextField
                    label={t('confirm.evidenceLabel')}
                    value={draft.evidenceRef}
                    onChange={(event) => patch(row.layerId, { evidenceRef: event.target.value })}
                    disabled={locked || sending}
                    error={draft.evidenceRef.length > EVIDENCE_MAX}
                    helperText={t('confirm.evidenceHint')}
                    slotProps={{ htmlInput: { maxLength: EVIDENCE_MAX + 1, 'aria-label': `${t('confirm.evidenceLabel')} ${row.sku}` } }}
                  />
                </Box>

                {check.isZero && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    {t('confirm.zeroWarning')}
                  </Alert>
                )}
              </Paper>
            );
          })}
        </Stack>

        <Paper variant="outlined" sx={{ p: 2, mt: 3 }} aria-live="polite" data-testid="confirm-summary">
          <Typography variant="subtitle2" gutterBottom>
            {t('confirm.summaryTitle')}
          </Typography>
          <Typography variant="body2">{t('confirm.summaryCount', { count: rows.length, ready: typedCount, units: remainingTotal })}</Typography>
          <Typography variant="body2">
            {typedCount === rows.length
              ? t('confirm.summaryTotal', { value: formatEuroFromCents(roundedTotal, language) })
              : t('confirm.summaryTotalPartial', { value: formatEuroFromCents(roundedTotal, language), ready: typedCount, count: rows.length })}
          </Typography>
          {typedCount > 0 && formatEuroFromCents4(exactTotal, language) !== formatEuroFromCents(roundedTotal, language) && (
            <Typography variant="caption" color="text.secondary">
              {t('confirm.summaryExact', { value: formatEuroFromCents4(exactTotal, language) })}
            </Typography>
          )}
          {ackNeeded && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
              {t('confirm.summaryConsumed', { count: consumedRows.length, units: consumedTotal })}
            </Typography>
          )}
        </Paper>

        {ackNeeded && (
          <FormControlLabel
            sx={{ mt: 2, alignItems: 'flex-start' }}
            control={<Checkbox checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} disabled={locked || sending} />}
            label={t('confirm.acknowledge', { units: consumedTotal, count: consumedRows.length })}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {flow.pending && flow.phase !== 'sending' ? (
          <>
            <Button color="error" onClick={flow.discard}>
              {t('pending.discard')}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={onClose}>{tc('close')}</Button>
            <Button variant="contained" onClick={() => void flow.retry()}>
              {t('pending.retry')}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={sending}>
              {tc('cancel')}
            </Button>
            <Button variant="contained" onClick={submit} disabled={!canSubmit} startIcon={sending ? <CircularProgress size={16} color="inherit" /> : undefined}>
              {sending ? t('confirm.sending') : t('confirm.submit', { count: rows.length })}
            </Button>
          </>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
}

function HintChip(props: { source: HintSource; label: string; enabled: boolean; useLabel: string; onUse: () => void }) {
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }} data-hint={props.source}>
      <Chip size="small" variant="outlined" label={props.label} />
      <Button size="small" disabled={!props.enabled} onClick={props.onUse} aria-label={`${props.useLabel}: ${props.label}`}>
        {props.useLabel}
      </Button>
    </Stack>
  );
}
